/**
 * Centralized capability policy for Compatibility Mode (ClickUp 86e2dbnbj,
 * change-set D2.1) — THE canonical protected-capability vocabulary. Every
 * client surface that gates a protected action consults this module (via
 * `GovernanceState.can()`), never ad-hoc mode checks; the Firestore rules
 * enforce the same matrix at the authoritative boundary (firestore.rules,
 * `compatWritesBlocked`), so hiding UI is presentation, not the control.
 *
 * Semantics mirror the fail-closed resolver: FULL_V3 allows everything here
 * (existing role/membership/license checks still apply upstream); a null mode
 * (account not in the governance program, or state indeterminate) means
 * governance enforcement is not active; V2_COMPATIBILITY_MODE blocks all
 * persistent protected writes except the explicitly allowlisted bounded
 * workflows.
 */
import { AccountAccessMode } from 'src/app/domain/governance/types/governance.types';

/** Canonical protected-capability vocabulary — one name per gated surface family. */
export type ProtectedCapability =
  | 'STUDENT_WRITE'
  | 'GOAL_WRITE'
  | 'OBSERVATION_WRITE'
  | 'ATTACHMENT_WRITE'
  | 'WORK_PRODUCT_WRITE'
  | 'AI_GENERATION'
  | 'BACKGROUND_SYNC'
  | 'PROTECTED_EXPORT'
  | 'EPHEMERAL_PDF_EXPORT'
  | 'VIEW_EXISTING_RECORDS';

/** Every capability, for exhaustive iteration (tests, inventories, docs). */
export const PROTECTED_CAPABILITIES: readonly ProtectedCapability[] = [
  'STUDENT_WRITE',
  'GOAL_WRITE',
  'OBSERVATION_WRITE',
  'ATTACHMENT_WRITE',
  'WORK_PRODUCT_WRITE',
  'AI_GENERATION',
  'BACKGROUND_SYNC',
  'PROTECTED_EXPORT',
  'EPHEMERAL_PDF_EXPORT',
  'VIEW_EXISTING_RECORDS',
];

/** Disposition of one capability under Compatibility Mode, with the why. */
export interface CapabilityDisposition {
  compatMode: 'blocked' | 'allowed';
  rationale: string;
}

/**
 * Static inventory (D2.1): every capability's disposition under
 * Compatibility Mode. The unit suite asserts completeness.
 */
export const CAPABILITY_DISPOSITIONS: Record<ProtectedCapability, CapabilityDisposition> = {
  STUDENT_WRITE: {
    compatMode: 'blocked',
    rationale: 'Student create/update/delete/archive persists protected records; blocked at Firestore rules too.',
  },
  GOAL_WRITE: {
    compatMode: 'blocked',
    rationale: 'Goal drafting/finalizing/editing persists IEP work product; the ephemeral PDF flow replaces it.',
  },
  OBSERVATION_WRITE: {
    compatMode: 'blocked',
    rationale: 'Observation records are protected student data; the ephemeral data-collection PDF replaces it.',
  },
  ATTACHMENT_WRITE: {
    compatMode: 'blocked',
    rationale: 'Uploads/evidence persist protected content to Storage; no compat allowlist exists for them.',
  },
  WORK_PRODUCT_WRITE: {
    compatMode: 'blocked',
    rationale: 'Reports, notes, and other protected work products are persistent writes.',
  },
  AI_GENERATION: {
    compatMode: 'blocked',
    rationale: 'AI entry points read/derive protected data and persist generations; out of compat scope.',
  },
  BACKGROUND_SYNC: {
    compatMode: 'blocked',
    rationale:
      'Server-bound autosave and offline write queues must never move compat content toward the platform. ' +
      'Device-local storage (drafts, recovery) is permitted — local is private to the user (decision 2026-07-19).',
  },
  PROTECTED_EXPORT: {
    compatMode: 'blocked',
    rationale: 'Persistent/platform-stored exports are writes; only the ephemeral PDF download is sanctioned.',
  },
  EPHEMERAL_PDF_EXPORT: {
    compatMode: 'allowed',
    rationale: 'The explicitly allowlisted bounded workflow: in-session entry to a downloaded PDF, nothing persisted.',
  },
  VIEW_EXISTING_RECORDS: {
    compatMode: 'allowed',
    rationale: 'Reads remain per existing permissions pending Product decision D2.8 on record-view scope.',
  },
};

/**
 * The one capability gate. FULL_V3 and governance-inactive accounts pass;
 * Compatibility Mode passes only capabilities the inventory allowlists.
 */
export function isCapabilityAllowed(mode: AccountAccessMode | null, capability: ProtectedCapability): boolean {
  if (mode !== 'V2_COMPATIBILITY_MODE') {
    return true;
  }
  return CAPABILITY_DISPOSITIONS[capability].compatMode === 'allowed';
}

/** Plain-language, account-scoped explanation for a blocked capability. */
export function blockedCapabilityExplanation(capability: ProtectedCapability): string {
  const actions: Record<ProtectedCapability, string> = {
    STUDENT_WRITE: 'Adding or changing students',
    GOAL_WRITE: 'Saving goals',
    OBSERVATION_WRITE: 'Saving observations',
    ATTACHMENT_WRITE: 'Uploading files',
    WORK_PRODUCT_WRITE: 'Saving reports and notes',
    AI_GENERATION: 'AI assistance',
    BACKGROUND_SYNC: 'Automatic saving',
    PROTECTED_EXPORT: 'Saved exports',
    EPHEMERAL_PDF_EXPORT: 'PDF download',
    VIEW_EXISTING_RECORDS: 'Viewing records',
  };
  return (
    `${actions[capability]} is unavailable while your account has not accepted the latest version of the ` +
    `Blume App. ` +
    `Nothing you enter here is saved to the platform. Your account's designated Lead ` +
    `Administrator can accept the latest version at any time to unlock it.`
  );
}
